import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';

import {
  clampStageRect,
  clampsFor,
  stageRect,
  toImageRect,
  toStageRect,
  type Fit,
  type ImageRect,
  type Size,
  type StageRect,
} from '../../lib/geometry';

/** Which corner is being dragged. Mirrors the `data-handle` attribute on the overlay. */
export type HandleId = 'nw' | 'ne' | 'sw' | 'se';

const HANDLES: readonly HandleId[] = ['nw', 'ne', 'sw', 'se'];
const isHandle = (value: string | null): value is HandleId =>
  value !== null && (HANDLES as readonly string[]).includes(value);

type Gesture =
  | { mode: 'move'; start: StageRect; px: number; py: number }
  | { mode: 'scale'; start: StageRect; px: number; py: number; handle: HandleId }
  | { mode: 'pinch'; start: StageRect; distance: number; mx: number; my: number };

interface Options {
  stageRef: React.RefObject<HTMLElement | null>;
  stage: Size | null;
  fit: Fit | null;
  board: ImageRect | null;
  onBoard: (board: ImageRect) => void;
}

export interface GestureHandlers {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
}

/**
 * Drag, corner-resize, pinch and scroll-wheel resize for the board.
 *
 * All arithmetic happens in **stage pixels**, because that is the space the pointer lives in
 * and the space the clamps are defined in. The result is converted to image pixels before it
 * reaches state (ADR-0011), so the board never moves when the stage does.
 *
 * Ported from the prototype's gesture maths, including the corner-handle signs and the
 * pinch-around-midpoint formula.
 */
export function useBoardGestures({
  stageRef,
  stage,
  fit,
  board,
  onBoard,
}: Options): GestureHandlers {
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<Gesture | null>(null);

  // Latest values, read inside handlers so the callbacks stay stable across renders.
  // Synced in an effect rather than during render: effects run at commit, well before any
  // pointer event can fire, so the handlers never see a stale frame.
  const latest = useRef({ stage, fit, board, onBoard });
  useEffect(() => {
    latest.current = { stage, fit, board, onBoard };
  });

  /** Clamp in stage space, convert to image space, publish. */
  const commit = useCallback((next: StageRect): void => {
    const { stage: s, fit: f, onBoard: publish } = latest.current;
    if (s === null || f === null) return;
    publish(toImageRect(clampStageRect(next, s), f));
  }, []);

  /** The board as it currently sits on the stage. */
  const currentStageRect = useCallback((): StageRect | null => {
    const { fit: f, board: b } = latest.current;
    return f === null || b === null ? null : toStageRect(b, f);
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>): void => {
      const start = currentStageRect();
      if (start === null) return;

      pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      event.currentTarget.setPointerCapture(event.pointerId);

      const target = event.target as HTMLElement;
      const handle = target.getAttribute?.('data-handle') ?? null;

      if (pointers.current.size >= 2) {
        const [a, b] = [...pointers.current.values()];
        if (a === undefined || b === undefined) return;
        gesture.current = {
          mode: 'pinch',
          start,
          distance: Math.hypot(a.x - b.x, a.y - b.y),
          mx: (a.x + b.x) / 2,
          my: (a.y + b.y) / 2,
        };
        return;
      }

      gesture.current = isHandle(handle)
        ? { mode: 'scale', start, px: event.clientX, py: event.clientY, handle }
        : { mode: 'move', start, px: event.clientX, py: event.clientY };
    },
    [currentStageRect],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>): void => {
      const active = gesture.current;
      const { stage: s } = latest.current;
      if (active === null || s === null || !pointers.current.has(event.pointerId)) return;

      pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const { minSize, maxSize } = clampsFor(s);
      const clampSize = (value: number): number => Math.min(maxSize, Math.max(minSize, value));

      if (active.mode === 'move') {
        commit(
          stageRect(
            active.start.x + (event.clientX - active.px),
            active.start.y + (event.clientY - active.py),
            active.start.size,
          ),
        );
        return;
      }

      if (active.mode === 'scale') {
        const dx = event.clientX - active.px;
        const dy = event.clientY - active.py;

        // Signed drag along the handle's own diagonal: pulling away from the board grows it.
        const along =
          (active.handle === 'se'
            ? dx + dy
            : active.handle === 'nw'
              ? -(dx + dy)
              : active.handle === 'ne'
                ? dx - dy
                : dy - dx) / 2;

        const size = clampSize(active.start.size + along * 2);
        const grew = size - active.start.size;

        // Pin the opposite corner: dragging nw/sw moves the left edge, nw/ne the top edge.
        commit(
          stageRect(
            active.handle === 'nw' || active.handle === 'sw'
              ? active.start.x - grew
              : active.start.x,
            active.handle === 'nw' || active.handle === 'ne'
              ? active.start.y - grew
              : active.start.y,
            size,
          ),
        );
        return;
      }

      if (pointers.current.size < 2) return;
      const [a, b] = [...pointers.current.values()];
      if (a === undefined || b === undefined) return;

      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      const size = clampSize(active.start.size * (distance / (active.distance || 1)));
      const k = size / active.start.size;

      // Scale about the gesture midpoint so the picture stays under the fingers.
      commit(
        stageRect(
          active.mx - (active.mx - active.start.x) * k,
          active.my - (active.my - active.start.y) * k,
          size,
        ),
      );
    },
    [commit],
  );

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>): void => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size === 0) gesture.current = null;
  }, []);

  // Wheel is attached manually: React's onWheel is passive, so preventDefault there would
  // warn and do nothing, and the page would scroll while resizing the board.
  useEffect(() => {
    const element = stageRef.current;
    if (element === null) return;

    const onWheel = (event: WheelEvent): void => {
      const { stage: s, fit: f, board: b } = latest.current;
      if (s === null || f === null || b === null) return;

      event.preventDefault();

      const current = toStageRect(b, f);
      const { minSize, maxSize } = clampsFor(s);
      const size = Math.min(maxSize, Math.max(minSize, current.size - event.deltaY * 0.6));
      const grew = current.size - size;

      // Resize about the board's centre.
      commit(stageRect(current.x + grew / 2, current.y + grew / 2, size));
    };

    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [stageRef, commit]);

  return { onPointerDown, onPointerMove, onPointerUp };
}
